import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { multipartUpload } from '../lib/multipartUpload';
import { useAuth } from '../context/AuthContext';
import TranscriptDownloads from '../components/TranscriptDownloads';
import ShareLinkManager from '../components/ShareLinkManager';
import { ACCEPT_ATTR, SUPPORTED_HINT, isMediaFile, mediaKindOf, extLabel } from '../lib/mediaKind';
import { FaceCard, useFaceSave } from '../components/FaceNamer';

const MAX_PARALLEL_UPLOADS = 3;

// Audio clips have no frame to thumbnail, so they get a deliberate flat slot in
// the page accent carrying the extension — the same treatment the Storage grid
// gives a file with no thumbnail. Never a broken poster.
function AudioTile({ fileName, width, height, radius = 5, fontSize = 9 }) {
  return (
    <div
      title={fileName}
      style={{
        width, height, borderRadius: radius, flexShrink: 0,
        background: 'rgba(139,109,255,0.12)',
        border: '1px solid rgba(139,109,255,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 700, letterSpacing: 0.5, color: 'var(--violet)',
      }}
    >{extLabel(fileName)}</div>
  );
}

const MATCH_STATUS_LABELS = {
  unmatched:      'Unmatched',
  pending_review: 'Pending Review',
  matched:        'Matched',
  no_match:       'No Match',
};

const MATCH_STATUS_COLORS = {
  unmatched:      'var(--tx-muted)',
  pending_review: 'var(--violet)',
  matched:        'var(--s-approved-t)',
  no_match:       'var(--tx-faint)',
};

function formatBytes(b) {
  const n = Number(b);
  if (!n) return '—';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPct(n) {
  return `${Math.round(Number(n) * 100)}%`;
}

// ─── Upload zone ──────────────────────────────────────────────────────────────

function UploadZone({ clients, onUploaded }) {
  const { user } = useAuth();
  const [dragOver,    setDragOver]    = useState(false);
  const [clientId,    setClientId]    = useState('');
  const [queue,       setQueue]       = useState([]); // { id, file, state, progress, error }
  const fileInput = useRef();

  // In-flight tracking — refs because we need synchronous reads inside async flow.
  const activeCount       = useRef(0);
  const pendingQueue      = useRef([]);          // [{ id, file }]
  const uploadControllers = useRef({});          // id → multipartUpload controller

  const canUpload = user?.roles?.some(r =>
    ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'].includes(r)
  ) || (user?.isExternal && user?.assignedClientIds?.length);

  const effectiveClientId = user?.isExternal && user?.assignedClientIds?.length
    ? user.assignedClientIds[0]
    : clientId;

  async function runUpload(id, file) {
    setQueue(q => q.map(i => i.id === id ? { ...i, state: 'uploading', progress: 0, error: undefined } : i));

    let footageId;
    try {
      const upload = multipartUpload({
        file,
        api,
        endpoints: {
          start:    '/api/footage/multipart/start',
          signPart: '/api/footage/multipart/sign-part',
          complete: '/api/footage/multipart/complete',
          abort:    '/api/footage/multipart/abort',
        },
        startBody:   { clientId: effectiveClientId },
        idFieldName: 'footageId',
        onProgress: (loaded, total) => {
          const pct = Math.round((loaded / total) * 100);
          setQueue(q => q.map(i => i.id === id ? { ...i, progress: pct } : i));
        },
      });
      uploadControllers.current[id] = upload;

      const result = await upload.promise;
      footageId = result.footageId;

      // Verification loop: /confirm HEADs the object in S3 and checks it matches
      // the source file's size before marking the clip usable.
      setQueue(q => q.map(i => i.id === id ? { ...i, state: 'verifying', progress: 100 } : i));
      const { data } = await api.post('/api/footage/confirm', { footageId });

      const dupNote = data?.isPossibleDuplicate
        ? `Possible duplicate${data.duplicateOfName ? ` of ${data.duplicateOfName}` : ''} — kept and flagged for review.`
        : undefined;

      if (data?.verified === false) {
        setQueue(q => q.map(i => i.id === id
          ? { ...i, state: 'verify_failed', error: data?.verification?.error || 'Verification failed — the uploaded file does not match the source.', warning: dupNote }
          : i));
      } else {
        setQueue(q => q.map(i => i.id === id ? { ...i, state: 'complete', warning: dupNote } : i));
      }
      onUploaded();
    } catch (err) {
      const isCancel = err?.message === 'cancelled';
      setQueue(q => q.map(i => i.id === id
        ? { ...i, state: isCancel ? 'cancelled' : 'error', error: isCancel ? undefined : (err?.message || 'Upload failed') }
        : i,
      ));
    } finally {
      delete uploadControllers.current[id];
    }
  }

  function pumpQueue() {
    while (activeCount.current < MAX_PARALLEL_UPLOADS && pendingQueue.current.length > 0) {
      const next = pendingQueue.current.shift();
      activeCount.current++;
      runUpload(next.id, next.file).finally(() => {
        activeCount.current--;
        pumpQueue();
      });
    }
  }

  function enqueueFiles(files) {
    // External talent must always upload into their assigned client; internal
    // users may leave it unassigned and tag the client during review.
    if (!effectiveClientId && user?.isExternal) {
      alert('Please select a project before uploading.');
      return;
    }
    const accepted = [...files].filter(f => isMediaFile(f.name));
    if (!accepted.length) return;

    const newItems = accepted.map(file => ({
      id: `${Date.now()}_${Math.random()}`,
      file,
      state: 'queued',
      progress: 0,
    }));
    setQueue(q => [...q, ...newItems]);
    newItems.forEach(item => pendingQueue.current.push({ id: item.id, file: item.file }));
    pumpQueue();
  }

  function cancelItem(id) {
    uploadControllers.current[id]?.cancel();
    // If it's still in the pending queue (not yet started), drop it from there too.
    pendingQueue.current = pendingQueue.current.filter(p => p.id !== id);
    setQueue(q => q.map(i => i.id === id && i.state === 'queued' ? { ...i, state: 'cancelled' } : i));
  }

  function retryItem(id) {
    const item = queue.find(i => i.id === id);
    if (!item) return;
    setQueue(q => q.map(i => i.id === id ? { ...i, state: 'queued', progress: 0, error: undefined } : i));
    pendingQueue.current.push({ id, file: item.file });
    pumpQueue();
  }

  function dismissItem(id) {
    setQueue(q => q.filter(i => i.id !== id));
  }

  function handleFiles(files) {
    enqueueFiles(files);
  }

  if (!canUpload) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      {!user?.isExternal && (
        <div style={{ marginBottom: 10 }}>
          <select
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)', fontSize: 13, fontFamily: 'var(--font)', minWidth: 200 }}
          >
            <option value="">No project — tag later</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span style={{ fontSize: 11, color: 'var(--tx-faint)', marginLeft: 10 }}>
            Optional — assign now or while reviewing.
          </span>
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInput.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--violet)' : 'var(--border-mid)'}`,
          borderRadius: 12,
          padding: '24px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'rgba(139,109,255,0.06)' : 'var(--bg-card2)',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 24, marginBottom: 8 }}>⬡</div>
        <div style={{ fontSize: 13, color: 'var(--tx-secondary)', fontWeight: 600 }}>
          Drop footage or audio here, or click to browse
        </div>
        <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
          {SUPPORTED_HINT}
        </div>
        <input ref={fileInput} type="file" accept={ACCEPT_ATTR} multiple style={{ display: 'none' }}
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {queue.length > 0 && (() => {
        const doneCount   = queue.filter(i => i.state === 'complete').length;
        const errorCount  = queue.filter(i => i.state === 'error' || i.state === 'verify_failed').length;
        const dupCount    = queue.filter(i => i.state === 'complete' && i.warning).length;
        const activeCount = queue.length - doneCount - errorCount - queue.filter(i => i.state === 'cancelled').length;
        const active = queue.filter(i => i.state !== 'complete');
        const done   = queue.filter(i => i.state === 'complete');
        return (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 6 }}>
            {doneCount} of {queue.length} uploaded &amp; verified
            {activeCount > 0 && <span> · {activeCount} in progress</span>}
            {errorCount > 0 && <span style={{ color: 'var(--s-incomplete-t)' }}> · {errorCount} failed verification</span>}
            {dupCount > 0 && <span style={{ color: 'var(--s-pending-t, var(--violet))' }}> · {dupCount} possible duplicate{dupCount > 1 ? 's' : ''}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
          {[...active, ...done].map(item => (
            <div key={item.id} style={{ background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</div>
                <div style={{ fontSize: 11, color: item.state === 'complete' ? 'var(--s-approved-t)' : (item.state === 'error' || item.state === 'verify_failed') ? 'var(--s-incomplete-t)' : 'var(--tx-muted)' }}>
                  {item.state === 'queued'        ? 'Queued…' :
                   item.state === 'uploading'     ? `Uploading ${item.progress}%` :
                   item.state === 'confirming'    ? 'Processing…' :
                   item.state === 'verifying'     ? 'Verifying…' :
                   item.state === 'complete'      ? '✓ Uploaded & verified' :
                   item.state === 'cancelled'     ? 'Cancelled' :
                   item.state === 'verify_failed' ? `⚠ ${item.error || 'Verification failed'}` :
                   item.state === 'error'         ? (item.error || 'Upload failed') : item.state}
                </div>
                {item.warning && (item.state === 'complete' || item.state === 'verify_failed') && (
                  <div style={{ fontSize: 11, color: 'var(--violet)', marginTop: 2 }}>⧉ {item.warning}</div>
                )}
              </div>
              {item.state === 'uploading' && (
                <div style={{ width: 80, height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{ height: '100%', width: `${item.progress}%`, background: 'var(--violet)', borderRadius: 2, transition: 'width 0.2s' }} />
                </div>
              )}
              {(item.state === 'uploading' || item.state === 'queued') && (
                <button
                  onClick={() => cancelItem(item.id)}
                  style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--tx-muted)', cursor: 'pointer', fontSize: 11, padding: '2px 8px', borderRadius: 6, flexShrink: 0, lineHeight: 1.4 }}
                >Cancel</button>
              )}
              {(item.state === 'error' || item.state === 'cancelled' || item.state === 'verify_failed') && (
                <button
                  onClick={() => retryItem(item.id)}
                  style={{ background: 'none', border: '1px solid var(--violet)', color: 'var(--violet)', cursor: 'pointer', fontSize: 11, padding: '2px 8px', borderRadius: 6, flexShrink: 0, lineHeight: 1.4 }}
                >Retry</button>
              )}
              <button
                onClick={() => dismissItem(item.id)}
                style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 12, padding: '2px 4px', flexShrink: 0, lineHeight: 1 }}
              >✕</button>
            </div>
          ))}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ─── Script preview modal ─────────────────────────────────────────────────────

const STOP_WORDS = new Set(['that','this','with','from','have','been','will','were','they','their','when','what','which','your','into','than','then','also','more','about','some','would','could','should','there','these','those','other','after','before','where','while','being','just','like','over','even','only','such','very','each','both','most','here','well','much','many','same','made','make','take','come','know','time','look','said','want','need','good','back']);

function buildHighlightRegex(transcriptText) {
  if (!transcriptText) return null;
  const words = transcriptText
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 4 && !STOP_WORDS.has(w));
  const unique = [...new Set(words)].slice(0, 30);
  if (!unique.length) return null;
  return new RegExp(`(${unique.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
}

// Renders text with two layers of decoration:
//   1. http/https URLs become clickable <a target="_blank"> links
//   2. Words matching the transcript-highlight regex get a <mark> wrap; the
//      pass is skipped inside URL segments.
function HighlightedText({ text, regex }) {
  // Defensive: callers should only pass strings, but a stray object/array cell
  // value would otherwise make text.split() throw and blank the page.
  if (typeof text !== 'string') text = text == null ? '' : String(text);
  if (!text) return <span>{text}</span>;
  // Split on URLs; the captured group keeps the URL in the output array.
  const urlParts = text.split(/(https?:\/\/[^\s<>"')]+)/g);
  return (
    <>
      {urlParts.map((part, i) => {
        if (!part) return null;
        const isUrl = /^https?:\/\//.test(part);
        if (isUrl) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--violet)', textDecoration: 'underline', wordBreak: 'break-all' }}
              onClick={e => e.stopPropagation()}
            >{part}</a>
          );
        }
        if (!regex) return <span key={i}>{part}</span>;
        const hlRe = new RegExp(regex.source, regex.flags);
        const segments = part.split(hlRe);
        return (
          <span key={i}>
            {segments.map((seg, j) => {
              const matchRe = new RegExp(regex.source, regex.flags);
              return matchRe.test(seg)
                ? <mark key={j} style={{ background: 'rgba(139,109,255,0.28)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{seg}</mark>
                : <span key={j}>{seg}</span>;
            })}
          </span>
        );
      })}
    </>
  );
}

function ScriptPreviewModal({ scriptId, transcriptText, side = 'right', onConfirm, confirmLabel, confirming, onClose }) {
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const hlRegex = buildHighlightRegex(transcriptText);

  useEffect(() => {
    api.get(`/api/scripts/${scriptId}`)
      .then(r => setScript(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scriptId]);

  const scenes = script?.scriptBody?.scenes || [];

  // Columns come from the first scene's keys, but internal row metadata must be
  // skipped: `id`, the per-row `qualNotes`, and any underscore-prefixed key
  // (_rowColor, _cellColors, _links, _propsItems). The underscore keys hold
  // objects/arrays — feeding a non-string into <HighlightedText> throws and
  // blanks the whole page (the manual-match preview crash).
  const colHeaders = scenes.length > 0
    ? Object.keys(scenes[0]).filter(k => k !== 'qualNotes' && k !== 'id' && !k.startsWith('_'))
    : [];

  const align = side === 'left' ? 'flex-start' : 'flex-end';
  const panelBorder = side === 'left' ? { borderRight: '1px solid var(--border)' } : { borderLeft: '1px solid var(--border)' };
  const panelShadow = side === 'left' ? '8px 0 32px rgba(0,0,0,0.3)' : '-8px 0 32px rgba(0,0,0,0.3)';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: align, pointerEvents: 'none' }}>
      <div
        style={{ width: '50vw', maxWidth: 800, height: '100vh', background: 'var(--bg-card)', ...panelBorder, display: 'flex', flexDirection: 'column', pointerEvents: 'all', boxShadow: panelShadow }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 2 }}>
              {onConfirm ? 'Script Preview — confirm to link this footage' : 'Script Preview — read only'}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {loading ? 'Loading…' : (script?.scriptName || 'Untitled')}
            </div>
            {script && (
              <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 3 }}>
                {[script.client?.name || script.clientName, script.scriptBody?.format || script.scriptType, script.qualStatus].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <button onClick={onClose} disabled={confirming} style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: confirming ? 'wait' : 'pointer', fontSize: 16, lineHeight: 1, padding: 4, flexShrink: 0 }}>✕</button>
        </div>

        {hlRegex && (
          <div style={{ flexShrink: 0, padding: '8px 20px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--violet)', background: 'rgba(139,109,255,0.06)' }}>
            Highlighted words match terms found in the transcript
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading && <div style={{ color: 'var(--tx-faint)', fontSize: 13 }}>Loading script…</div>}
          {!loading && !script && <div style={{ color: 'var(--tx-faint)', fontSize: 13 }}>Script not found.</div>}
          {!loading && script && scenes.length === 0 && (
            <div style={{ color: 'var(--tx-faint)', fontSize: 13 }}>No script content yet.</div>
          )}
          {!loading && script && scenes.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {colHeaders.map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, color: 'var(--tx-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      {h.replace(/([A-Z])/g, ' $1').trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scenes.map((scene, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                    {colHeaders.map(col => (
                      <td key={col} style={{ padding: '8px 10px', color: 'var(--tx-primary)', lineHeight: 1.5 }}>
                        <HighlightedText text={scene[col] || ''} regex={hlRegex} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Confirm CTA (only when onConfirm is provided) */}
        {onConfirm && (
          <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg-card2)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              className="btn-ghost"
              disabled={confirming}
              onClick={onClose}
              style={{ fontSize: 12 }}
            >Cancel</button>
            <button
              disabled={confirming || loading || !script}
              onClick={onConfirm}
              style={{ background: 'var(--violet)', border: 'none', borderRadius: 6, color: '#fff', cursor: (confirming || loading || !script) ? 'wait' : 'pointer', fontSize: 12, padding: '6px 14px', fontFamily: 'var(--font)', fontWeight: 600 }}
            >
              {confirming ? 'Linking…' : (confirmLabel || 'Use this script')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Manual script search (used when auto-match yields nothing) ──────────────

function ScriptSearch({ clientId, onPick }) {
  const [term, setTerm]         = useState('');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [picking, setPicking]   = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q });
        if (clientId) params.set('clientId', clientId);
        const r = await api.get(`/api/footage/scripts-search?${params.toString()}`);
        setResults(Array.isArray(r.data) ? r.data.slice(0, 8) : []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [term, clientId]);

  // Click-outside to close
  useEffect(() => {
    function onDocClick(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function pick(result) {
    setPicking(true);
    try { await onPick(result); }
    finally { setPicking(false); setOpen(false); setTerm(''); setResults([]); }
  }

  return (
    <div ref={boxRef} style={{ position: 'relative', marginTop: 12 }}>
      <input
        type="text"
        value={term}
        onChange={e => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={picking}
        placeholder={clientId ? 'Search scripts or Riff cards…' : 'Search scripts or Riff cards (all projects)…'}
        style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)' }}
      />
      {open && term.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 10, maxHeight: 260, overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--tx-faint)' }}>Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--tx-faint)' }}>No scripts or Riff cards match.</div>
          )}
          {!loading && results.map(s => {
            const isRiff = s.type === 'riff';
            const key   = isRiff ? `riff-${s.liveProductionId}` : `script-${s.id}`;
            const title = isRiff ? (s.title?.trim() || '(Untitled Riff)') : (s.scriptName || 'Untitled');
            const sub   = isRiff
              ? [s.client?.name, s.actorName, s.status].filter(Boolean).join(' · ')
              : [s.client?.name, s.format, s.qualStatus].filter(Boolean).join(' · ');
            return (
              <button
                key={key}
                type="button"
                disabled={picking}
                onClick={() => pick(s)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', background: 'none', border: 'none',
                  borderBottom: '1px solid var(--border)',
                  color: 'var(--tx-primary)', cursor: picking ? 'wait' : 'pointer', fontFamily: 'var(--font)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isRiff && (
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--violet)', background: 'rgba(139,109,255,0.15)', border: '1px solid var(--violet)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>RIFF</span>
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>{sub}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({ footageId, clients = [], onClose, onUpdated, onOpenFootage }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [footage,       setFootage]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [transcribing,  setTranscribing]  = useState(false);
  const [previewMatch,  setPreviewMatch]  = useState(null);
  const [searchPreview, setSearchPreview] = useState(null); // { scriptId } — preview-then-confirm from manual search
  const [confirmingSearch, setConfirmingSearch] = useState(false);
  const [unassigning,   setUnassigning]   = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Archive now MOVES the file to cold Storage, so it gets a confirm dialog.
  // `archivePreview` holds the server's answer for where it would go.
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [archiving,     setArchiving]     = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [clientChoice,  setClientChoice]  = useState('');
  const [savingClient,  setSavingClient]  = useState(false);
  // ADC (Automated Dailies Cutter): { adc_status, adc_parsed_title, adc_error, adc_completed_at, clips }
  const [adc,           setAdc]           = useState(null);
  const [adcStarting,   setAdcStarting]   = useState(false);
  const [clipsOpen,     setClipsOpen]     = useState(true);
  const txPollRef  = useRef(null);
  const adcPollRef = useRef(null);
  const videoRef   = useRef(null);
  // Rough Cut v9 (D-153) · Faces UI (D-154): NAME THE FACES — the eye's person tags on this clip and the client's character registry;
  // the cards, choices (registry + Director / Crew) and Save / Clear states live in components/FaceNamer.jsx (shared with the production card)
  const [faceChars, setFaceChars] = useState(null);

  const canEdit = user?.roles?.some(r =>
    ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'].includes(r)
  );
  const isAdmin = user?.roles?.some(r => ['Admin', 'Power User', 'Power_User'].includes(r));
  const isUploader = !!user?.id && !!footage?.uploadedBy && footage.uploadedBy === user.id;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/footage/${footageId}`);
      setFootage(r.data);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [footageId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!footage?.visualFaces?.length || !footage?.registryClientId || !footage?.canNameFaces) return;
    api.get(`/api/client-characters?clientId=${footage.registryClientId}`).then(r => setFaceChars((r.data?.characters || []).filter(c => !c.isArchived))).catch(() => setFaceChars([]));
  }, [footage?.registryClientId, footage?.visualFaces?.length, footage?.canNameFaces]);
  const seekPlayer = (t) => { const v = videoRef.current; if (!v) return; try { v.currentTime = t; v.pause(); v.scrollIntoView({ block: 'nearest' }); } catch {} };
  const faceSaver = useFaceSave(footageId, faces => setFootage(prev => prev ? { ...prev, visualFaces: faces } : prev));

  // Poll transcription if in-progress
  useEffect(() => {
    const tx = footage?.transcription;
    if (tx?.status === 'pending' || tx?.status === 'processing') {
      txPollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/api/transcriptions/raw_footage/${footageId}`);
          if (r.data?.status !== 'pending' && r.data?.status !== 'processing') {
            clearInterval(txPollRef.current);
          }
          setFootage(prev => prev ? { ...prev, transcription: r.data } : prev);
        } catch {}
      }, 8000);
    }
    return () => clearInterval(txPollRef.current);
  }, [footage?.transcription?.status, footageId]);

  // ── ADC status + child clips ──
  const loadAdc = useCallback(async () => {
    try {
      const r = await api.get(`/api/adc/${footageId}/status`);
      setAdc(r.data);
      return r.data;
    } catch { return null; }
  }, [footageId]);

  // Load ADC state once we know this is a parent (not a child clip).
  useEffect(() => {
    if (footage && !footage.parentFootageId) loadAdc();
  }, [footage?.id, footage?.parentFootageId, loadAdc]);

  // Poll while a job is queued/running (5s per spec), stop on terminal state.
  useEffect(() => {
    const st = adc?.adc_status;
    if (st === 'pending' || st === 'processing') {
      adcPollRef.current = setInterval(async () => {
        const data = await loadAdc();
        if (data && data.adc_status !== 'pending' && data.adc_status !== 'processing') {
          clearInterval(adcPollRef.current);
          onUpdated?.();   // refresh the library so the "✂ N clips" badge updates
        }
      }, 5000);
    }
    return () => clearInterval(adcPollRef.current);
  }, [adc?.adc_status, loadAdc, onUpdated]);

  async function handleConfirmMatch(matchId) {
    try {
      await api.patch(`/api/footage/${footageId}/matches/${matchId}/confirm`);
      await load();
      onUpdated();
    } catch (err) { alert(err.response?.data?.error || 'Failed to confirm match.'); }
  }

  async function handleRejectMatch(matchId) {
    try {
      await api.patch(`/api/footage/${footageId}/matches/${matchId}/reject`);
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to reject match.'); }
  }

  async function handleNoMatch() {
    try {
      await api.post(`/api/footage/${footageId}/no-match`);
      await load();
      onUpdated();
    } catch (err) { alert(err.response?.data?.error || 'Failed.'); }
  }

  async function handleManualMatchConfirm() {
    if (!searchPreview?.scriptId) return;
    setConfirmingSearch(true);
    try {
      await api.post(`/api/footage/${footageId}/manual-match`, { scriptId: searchPreview.scriptId });
      setSearchPreview(null);
      await load();
      onUpdated();
    } catch (err) { alert(err.response?.data?.error || 'Failed to link script.'); }
    finally { setConfirmingSearch(false); }
  }

  // Shared handler for the manual ScriptSearch box (empty-state pick and the
  // force-match-on-assigned-clip pick). Scripts go through preview-then-confirm;
  // Riff cards link directly after a confirm dialog (no transcript to preview).
  async function handleSearchPick(result) {
    if (result.type === 'riff') {
      const label = result.title?.trim() || 'Untitled Riff';
      if (!window.confirm(`Link this footage to Riff card "${label}"${result.client?.name ? ` (${result.client.name})` : ''}? This replaces any current match.`)) return;
      try {
        await api.post(`/api/footage/${footageId}/manual-match`, { liveProductionId: result.liveProductionId });
        await load();
        onUpdated();
      } catch (err) { alert(err.response?.data?.error || 'Failed to link Riff card.'); }
    } else {
      setSearchPreview({ scriptId: result.id });
    }
  }

  async function handleUnassign() {
    if (!window.confirm('Unlink this match? You can then pick a different script or Riff card.')) return;
    setUnassigning(true);
    try {
      await api.post(`/api/footage/${footageId}/unassign`);
      await load();
      onUpdated();
    } catch (err) { alert(err.response?.data?.error || 'Failed to unassign.'); }
    finally { setUnassigning(false); }
  }

  async function handleTranscribe() {
    setTranscribing(true);
    try {
      await api.post('/api/transcriptions', { source_type: 'raw_footage', source_id: footageId });
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Transcription failed to start.'); }
    finally { setTranscribing(false); }
  }

  async function handleProcessClips() {
    setAdcStarting(true);
    try {
      await api.post(`/api/adc/${footageId}/process`);
      await loadAdc();   // picks up 'pending' → kicks off polling
    } catch (err) {
      alert(err.response?.data?.error || 'Could not start clip processing.');
    } finally { setAdcStarting(false); }
  }

  async function handleDownload() {
    try {
      const r = await api.get(`/api/footage/${footageId}/download`);
      window.open(r.data.url, '_blank');
    } catch { alert('Download failed. Please try again.'); }
  }

  const [rematching, setRematching] = useState(false);

  function startEditClient() {
    setClientChoice(footage?.clientId || '');
    setEditingClient(true);
  }

  async function handleRematch() {
    setRematching(true);
    try {
      await api.post(`/api/footage/${footageId}/match`);
      await load();
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || 'Re-match failed.');
    } finally {
      setRematching(false);
    }
  }

  // BRAW originals (Context.HeliconflowBrawOriginals.md B-3): this MP4 is a proxy of <same basename>.braw — the cut-list download names the original
  const [savingOrig, setSavingOrig] = useState(false);
  async function handleOriginalExt(checked) {
    setSavingOrig(true);
    try { await api.patch(`/api/footage/${footageId}`, { originalExt: checked ? 'braw' : null }); await load(); onUpdated(); }
    catch (err) { alert(err.response?.data?.error || 'Failed to save the original format.'); }
    finally { setSavingOrig(false); }
  }

  async function handleSaveClient() {
    const nextClientId = clientChoice || null;
    if (nextClientId === (footage?.clientId || null)) {
      setEditingClient(false);
      return;
    }
    setSavingClient(true);
    try {
      const r = await api.patch(`/api/footage/${footageId}`, { clientId: nextClientId });
      setEditingClient(false);
      await load();
      onUpdated();
      if (r.data?.clientChanged && !r.data?.rematchTriggered) {
        // Transcript not yet ready — auto-match will fire on transcription completion.
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update project.');
    } finally {
      setSavingClient(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/api/footage/${footageId}`);
      setDeleteConfirm(false);
      onUpdated();
      onClose();
    } catch (err) {
      setDeleting(false);
      alert(err.response?.data?.error || 'Failed to delete footage.');
    }
  }

  // Ask the server where this clip would go, then show the confirm dialog. The
  // destination is resolved server-side so the dialog names the real client and
  // folder rather than guessing at them.
  async function openArchiveConfirm() {
    setArchiving(true);
    try {
      const res = await api.get(`/api/footage/${footageId}/archive-preview`);
      setArchiveConfirm(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not check where this clip would be archived.');
    } finally {
      setArchiving(false);
    }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await api.patch(`/api/footage/${footageId}/archive`);
      setArchiveConfirm(null);
      onUpdated();
      onClose();   // it leaves the active list — close back to the list
    } catch (err) {
      // Linked footage, and every verification failure, come back with an
      // explanatory message. A failure means nothing was moved or deleted.
      alert(err.response?.data?.error || 'Failed to archive footage.');
      setArchiveConfirm(null);
    } finally {
      setArchiving(false);
    }
  }

  async function handleUnarchive() {
    setArchiving(true);
    try {
      await api.patch(`/api/footage/${footageId}/unarchive`);
      await load();
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to unarchive footage.');
    } finally {
      setArchiving(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--tx-faint)', fontSize: 13 }}>Loading…</div>
  );
  if (!footage) return null;

  const tx = footage.transcription;
  const isAudio = mediaKindOf(footage) === 'audio';
  // The file's bytes were moved to the Storage vault — the row is kept, but
  // there is nothing hot left to play, and Unarchive alone cannot bring it back.
  const isColdArchived = !!footage.archivedToArchiveId;
  const pendingMatches = (footage.matches || []).filter(m => m.status === 'pending_review');
  const confirmedMatch = (footage.matches || []).find(m => m.status === 'confirmed');
  // Footage linked to a scriptless Riff card has no match row — surfaced separately.
  const linkedRiff = !confirmedMatch ? footage.linkedCard : null;

  return (
    <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {footage.parentFootageId && (
            <div
              onClick={() => onOpenFootage?.(footage.parentFootageId)}
              style={{ fontSize: 11, color: 'var(--violet)', cursor: 'pointer', marginBottom: 4 }}
              title="Back to the source footage"
            >
              ← Back to {footage.parentFileName || 'source footage'}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 3 }}>{footage.parentFootageId ? 'Scene Clip' : 'Raw Footage'}</div>
          <div style={{ fontSize: 14, color: 'var(--tx-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{footage.fileName}</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: MATCH_STATUS_COLORS[footage.matchStatus] }}>
              {MATCH_STATUS_LABELS[footage.matchStatus] || footage.matchStatus}
            </span>
            {editingClient ? (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <select
                  value={clientChoice}
                  onChange={e => setClientChoice(e.target.value)}
                  autoFocus
                  disabled={savingClient}
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 6, padding: '2px 6px', color: 'var(--tx-primary)', fontSize: 11, fontFamily: 'var(--font)' }}
                >
                  <option value="">Unassigned</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={handleSaveClient} disabled={savingClient} style={{ background: 'var(--violet)', border: 'none', borderRadius: 4, color: '#fff', cursor: savingClient ? 'wait' : 'pointer', fontSize: 11, padding: '2px 8px' }}>{savingClient ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setEditingClient(false)} disabled={savingClient} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--tx-muted)', cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>Cancel</button>
              </span>
            ) : (
              <span
                onClick={canEdit ? startEditClient : undefined}
                title={canEdit ? 'Click to change project (clears matches and re-runs auto-match)' : undefined}
                style={{ fontSize: 11, color: 'var(--tx-muted)', cursor: canEdit ? 'pointer' : 'default', textDecoration: canEdit ? 'underline dotted' : 'none', textDecorationColor: 'var(--border-mid)' }}
              >
                {footage.clientName || 'Unassigned'}
              </span>
            )}
            <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{formatBytes(footage.fileSizeBytes)}</span>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4, flexShrink: 0 }}>✕</button>
      </div>

      {/* Verification / duplicate banners */}
      {(footage.uploadStatus === 'failed' || footage.verificationStatus === 'mismatch' || footage.verificationStatus === 'missing') && (
        <div style={{ margin: '0 20px 4px', padding: '8px 12px', borderRadius: 8, background: 'rgba(255,90,90,0.1)', border: '1px solid rgba(255,90,90,0.28)', fontSize: 12, color: 'var(--s-incomplete-t)' }}>
          ⚠ <strong>Upload failed verification.</strong> {footage.verificationError || 'The stored file does not match the source. Re-upload this clip.'}
        </div>
      )}
      {footage.isPossibleDuplicate && (
        <div style={{ margin: '0 20px 4px', padding: '8px 12px', borderRadius: 8, background: 'rgba(139,109,255,0.1)', border: '1px solid rgba(139,109,255,0.28)', fontSize: 12, color: 'var(--violet)' }}>
          ⧉ <strong>Possible duplicate.</strong> This clip was uploaded under a name already in use
          {footage.duplicateOfName ? <> — likely a duplicate of <strong>{footage.duplicateOfName}</strong></> : ''} and was auto-renamed. Review and delete if it&apos;s the same footage.
        </div>
      )}
      {/* This clip came from the Storage cold archive rather than a direct upload. */}
      {footage.sourceArchiveId && (
        <div style={{ margin: '0 20px 4px', fontSize: 11, color: 'var(--tx-faint)' }}>
          From Storage archive —{' '}
          <Link
            to={`/storage?fileId=${footage.sourceArchiveId}`}
            style={{ color: 'var(--s-nr-t)', textDecoration: 'underline' }}
          >view the archived copy</Link>
        </div>
      )}

      {/* Archived to cold Storage — say so where the player used to be, rather
          than leaving a video element that will never load. */}
      {isColdArchived && (
        <div style={{ margin: '12px 20px 0', padding: '10px 12px', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--tx-secondary)', fontWeight: 600 }}>❄ Archived to Storage</div>
          <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4, lineHeight: 1.6 }}>
            This clip's file was moved to cold Storage, so it no longer plays here. The transcript and
            match history below are intact.{' '}
            <Link to={`/storage?fileId=${footage.archivedToArchiveId}`} style={{ color: 'var(--s-nr-t)', textDecoration: 'underline' }}>
              Open it in Storage
            </Link>{' '}— use Promote there to bring it back.
          </div>
        </div>
      )}

      {/* Preview player. Audio clips are browser-playable as uploaded, so they
          get a native <audio> element — no poster, no transcoding notice. */}
      {isColdArchived ? null : footage.playbackUrl && isAudio ? (
        <div style={{ padding: '12px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <AudioTile fileName={footage.fileName} width={56} height={40} radius={8} fontSize={11} />
          <audio
            key={footage.playbackUrl}
            controls
            preload="metadata"
            style={{ flex: 1, minWidth: 0, height: 40 }}
          >
            <source src={footage.playbackUrl} />
            Your browser doesn't support inline audio playback. Use Download to listen to this clip.
          </audio>
        </div>
      ) : footage.playbackUrl ? (
        <div style={{ padding: '12px 20px 0' }}>
          <video
            key={footage.playbackUrl}
            ref={videoRef}
            data-testid="footage-player"
            controls
            preload="metadata"
            poster={footage.thumbnailUrl || undefined}
            style={{ width: '100%', maxHeight: 260, borderRadius: 8, display: 'block', background: '#000' }}
          >
            <source src={footage.playbackUrl} />
            Your browser doesn't support inline video playback. Use Download to view this clip.
          </video>
          {footage.transcodingStatus && footage.transcodingStatus !== 'complete' && (
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 6 }}>
              {footage.transcodingStatus === 'pending' || footage.transcodingStatus === 'processing'
                ? 'Web-friendly preview still transcoding — original format may not play in all browsers.'
                : footage.transcodingStatus === 'failed'
                  ? 'Web preview transcoding failed — playing original file.'
                  : null}
            </div>
          )}
        </div>
      ) : footage.thumbnailUrl ? (
        <div style={{ padding: '12px 20px 0' }}>
          <img src={footage.thumbnailUrl} alt="" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 8, display: 'block', background: '#000' }} />
        </div>
      ) : null}

      {/* Rough Cut v9 (D-153) · Faces UI (D-154): NAME THE FACES — one card per person tag the eye found; the name is the eye's authority from then on */}
      {footage.visualFaces?.length > 0 && (
        <div data-testid="footage-faces" style={{ margin: '12px 20px 0', padding: '10px 12px', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-secondary)' }}>👁 Name the faces <span style={{ fontWeight: 400, color: 'var(--tx-faint)' }}>— the eye's people in this clip; a name here beats every inferred binding · Director / Crew never count as a performer</span></div>
          <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
            {footage.visualFaces.map(f => <FaceCard key={f.tag} face={f} footageId={footageId} chars={faceChars} canName={!!footage.canNameFaces} onSeek={seekPlayer} saver={faceSaver} />)}
          </div>
        </div>
      )}

      {/* BRAW originals (B-3): the per-clip original-format flag — parent .mp4 files only, the footage-edit roles */}
      {canEdit && !footage.parentFootageId && /\.mp4$/i.test(footage.fileName || '') && (
        <div style={{ margin: '12px 20px 0' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--tx-secondary)', cursor: savingOrig ? 'wait' : 'pointer' }}>
            <input type="checkbox" data-testid="footage-original-braw" checked={footage.originalExt === 'braw'} disabled={savingOrig} onChange={e => handleOriginalExt(e.target.checked)} />
            Proxy — original is BRAW
          </label>
          <div data-testid="footage-original-braw-help" style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>The proxy must keep the original's exact name and the same frame rate and length — a renamed proxy can't be re-pointed; a different frame rate misplaces every cut.</div>
        </div>
      )}

      {/* Actions */}
      <div style={{ padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <button className="btn-ghost" style={{ fontSize: 12 }} onClick={handleDownload}>↓ Download</button>

        {/* Transcription */}
        {(!tx || tx.status === 'failed') && (
          <button className="btn-ghost" style={{ fontSize: 12 }} disabled={transcribing} onClick={handleTranscribe}>
            {transcribing ? 'Starting…' : tx?.status === 'failed' ? '↺ Retry Transcribe' : '⊙ Transcribe'}
          </button>
        )}
        {(tx?.status === 'pending' || tx?.status === 'processing') && (
          <span style={{ fontSize: 12, color: 'var(--violet)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>◌</span> Transcribing…
          </span>
        )}

        {/* Public share links — sits beside Transcribe. Renders nothing at all
            for an external user (the component's own internal-only gate), and
            refuses to generate on a cold-archived clip, whose file has moved. */}
        <ShareLinkManager
          resourceType="footage"
          resourceId={footageId}
          disabledReason={isColdArchived
            ? 'This clip has been archived to Storage, so its file is no longer here and cannot be shared. Restore it from Storage first.'
            : null}
        />
        {tx?.status === 'complete' && (
          <span style={{ fontSize: 12, color: 'var(--s-approved-t)' }}>✓ Transcript ready</span>
        )}
        {canEdit && tx?.status === 'complete' && (
          <button
            className="btn-ghost"
            style={{ fontSize: 12 }}
            disabled={rematching}
            onClick={handleRematch}
            title="Clear pending matches and re-run auto-match against this clip's project"
          >
            {rematching ? 'Matching…' : '↺ Re-run Match'}
          </button>
        )}
        {/* Process Clips (ADC) — parents only, after upload completes. Any
            transcribed parent can be processed, including older uploads.
            Never audio: ADC cuts VIDEO scene clips, so the action is hidden on
            audio rows (the endpoint rejects them too). */}
        {footage.parentFootageId == null && footage.uploadStatus === 'complete' && !isAudio && (() => {
          const adcStatus = adc?.adc_status ?? footage.adcStatus ?? null;
          if (adcStatus === 'pending' || adcStatus === 'processing') {
            return (
              <span style={{ fontSize: 12, color: 'var(--violet)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>◌</span> Processing…
              </span>
            );
          }
          if (adcStatus === 'complete') {
            return <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>✓ Clips processed</span>;
          }
          const txReady = tx?.status === 'complete';
          return (
            <button
              className="btn-ghost"
              style={{ fontSize: 12, opacity: txReady ? 1 : 0.5, cursor: txReady ? 'pointer' : 'not-allowed' }}
              disabled={!txReady || adcStarting}
              title={txReady ? 'Extract scene clips from director action/cut cues' : 'Transcription required'}
              onClick={handleProcessClips}
            >
              {adcStarting ? 'Starting…' : '✂ Process Clips'}
            </button>
          );
        })()}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {canEdit && (
            footage.isArchived ? (
              <button
                className="btn-ghost"
                style={{ fontSize: 12 }}
                onClick={handleUnarchive}
                disabled={archiving || isColdArchived}
                title={isColdArchived
                  ? 'This clip\'s file is in cold Storage — restore it from Storage with Promote'
                  : 'Send this clip back to the active Raw Footage list'}
              >
                {archiving ? 'Unarchiving…' : '↩ Unarchive'}
              </button>
            ) : (
              <button
                className="btn-ghost"
                style={{ fontSize: 12 }}
                disabled={archiving}
                onClick={openArchiveConfirm}
                title={footage.linkedVideoCardId
                  ? 'Linked to a video card — archives automatically once that video is Posted'
                  : 'Move this clip\'s file to cold Storage'}
              >
                {archiving ? 'Archiving…' : '📁 Archive'}
              </button>
            )
          )}
          {(isAdmin || isUploader) && (
            <button
              className="btn-ghost"
              style={{ fontSize: 12, color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
              onClick={() => setDeleteConfirm(true)}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* ADC status + scene clips (parents only) */}
      {footage.parentFootageId == null && (adc?.adc_status || adc?.clips?.length > 0) && (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          {adc?.adc_status === 'processing' && (
            <div style={{ fontSize: 12, color: 'var(--violet)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>◌</span> Extracting scenes… this may take a few minutes
            </div>
          )}
          {adc?.adc_status === 'pending' && (
            <div style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Queued for clip extraction…</div>
          )}
          {adc?.adc_status === 'complete' && (
            <div style={{ fontSize: 12, color: 'var(--s-approved-t)' }}>
              ✓ {(adc.clips?.length || 0)} clip{adc.clips?.length === 1 ? '' : 's'} extracted
              {adc.adc_parsed_title ? ` — ${adc.adc_parsed_title}` : ''}
            </div>
          )}
          {adc?.adc_status === 'failed' && (
            <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)' }}>⚠ {adc.adc_error || 'Clip extraction failed.'}</div>
          )}

          {adc?.clips?.length > 0 && (
            <div style={{ marginTop: adc?.adc_status ? 12 : 0 }}>
              <div
                onClick={() => setClipsOpen(o => !o)}
                style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: clipsOpen ? 10 : 0 }}
              >
                <span style={{ fontSize: 10 }}>{clipsOpen ? '▾' : '▸'}</span>
                Scene Clips ({adc.clips.length})
              </div>
              {clipsOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {adc.clips.map(clip => (
                    <div
                      key={clip.id}
                      onClick={() => onOpenFootage?.(clip.id)}
                      title="Open this clip"
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-card2)', borderRadius: 8, cursor: 'pointer', border: '1px solid var(--border)' }}
                    >
                      {clip.thumbnailUrl
                        ? <img src={clip.thumbnailUrl} alt="" style={{ width: 48, height: 28, objectFit: 'cover', borderRadius: 4, flexShrink: 0, background: '#000' }} />
                        : mediaKindOf(clip) === 'audio'
                          ? <AudioTile fileName={clip.fileName} width={48} height={28} radius={4} />
                          : <div style={{ width: 48, height: 28, borderRadius: 4, flexShrink: 0, background: 'var(--bg-input)' }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 500 }}>{clip.sceneLabel || 'Scene'}</div>
                        <div style={{ fontSize: 11, color: 'var(--tx-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clip.fileName}</div>
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--tx-faint)', flexShrink: 0 }}>Open →</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Match candidates */}
      <div style={{ padding: '14px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Script Matches
        </div>

        {linkedRiff && (
          <div
            onClick={() => navigate(`/production/${linkedRiff.liveProductionId}`)}
            style={{
              padding: '10px 12px', background: 'rgba(139,109,255,0.08)',
              border: '1px solid rgba(139,109,255,0.3)', borderRadius: 8, marginBottom: 10, cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: 'var(--violet)', fontWeight: 600, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', background: 'rgba(139,109,255,0.15)', border: '1px solid var(--violet)', borderRadius: 4, padding: '1px 5px' }}>RIFF</span>
                ✓ Linked to Riff card
              </div>
              <span style={{ fontSize: 11, color: 'var(--violet)', opacity: 0.7 }}>Open in Live Production →</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--tx-primary)', fontWeight: 500 }}>{linkedRiff.title?.trim() || '(Untitled Riff)'}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
              {[linkedRiff.clientName, linkedRiff.actorName, linkedRiff.status].filter(Boolean).join(' · ')}
            </div>
            {canEdit && (
              <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                <button
                  className="btn-ghost"
                  disabled={unassigning}
                  onClick={handleUnassign}
                  style={{ fontSize: 11, padding: '3px 10px', color: 'var(--tx-muted)' }}
                  title="Unlink this Riff card so you can pick a different match"
                >
                  {unassigning ? 'Unlinking…' : '↶ Unlink / change'}
                </button>
              </div>
            )}
          </div>
        )}

        {confirmedMatch && (
          <div
            onClick={() => confirmedMatch.liveProductionId && navigate(`/production/${confirmedMatch.liveProductionId}`)}
            style={{
              padding: '10px 12px', background: 'rgba(72,190,110,0.08)',
              border: '1px solid rgba(72,190,110,0.25)', borderRadius: 8, marginBottom: 10,
              cursor: confirmedMatch.liveProductionId ? 'pointer' : 'default',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: 'var(--s-approved-t)', fontWeight: 600, marginBottom: 3 }}>✓ Confirmed Match</div>
              {confirmedMatch.liveProductionId && (
                <span style={{ fontSize: 11, color: 'var(--s-approved-t)', opacity: 0.7 }}>Open in Live Production →</span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--tx-primary)', fontWeight: 500 }}>{confirmedMatch.scriptName}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
              {confirmedMatch.clientName} · {Number(confirmedMatch.similarityScore) > 0 ? `${formatPct(confirmedMatch.similarityScore)} match` : 'manually linked'}
            </div>
            {canEdit && (
              <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                <button
                  className="btn-ghost"
                  disabled={unassigning}
                  onClick={handleUnassign}
                  style={{ fontSize: 11, padding: '3px 10px', color: 'var(--tx-muted)' }}
                  title="Unassign this script so you can pick a different one"
                >
                  {unassigning ? 'Unassigning…' : '↶ Unassign / change script'}
                </button>
              </div>
            )}
          </div>
        )}

        {canEdit && pendingMatches.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingMatches.map(m => (
              <div
                key={m.id}
                style={{ padding: '10px 12px', background: previewMatch?.id === m.id ? 'rgba(139,109,255,0.1)' : 'var(--bg-card2)', border: `1px solid ${previewMatch?.id === m.id ? 'var(--violet)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer' }}
                onClick={() => setPreviewMatch(previewMatch?.id === m.id ? null : m)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      #{m.rank} — {m.scriptName}
                    </div>
                    {String(m.matchMethod || '').startsWith('action_cut') && (
                      <div style={{ display: 'inline-block', marginTop: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--violet)', background: 'rgba(139,109,255,0.15)', border: '1px solid var(--violet)', borderRadius: 4, padding: '1px 5px' }}>
                        ⎯ MATCHED ON ACTION→CUT LINES{m.matchMethod === 'action_cut_ai' ? ' (AI)' : ''}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
                      {m.clientName} · {formatPct(m.similarityScore)} similarity · click to preview
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 11, padding: '4px 12px', color: 'var(--s-approved-t)', borderColor: 'rgba(72,190,110,0.3)' }}
                    onClick={() => handleConfirmMatch(m.id)}
                  >
                    ✓ Confirm
                  </button>
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 11, padding: '4px 12px', color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                    onClick={() => handleRejectMatch(m.id)}
                  >
                    ✕ Reject
                  </button>
                </div>
              </div>
            ))}

            <button
              className="btn-ghost"
              style={{ fontSize: 11, color: 'var(--tx-muted)', borderStyle: 'dashed', marginTop: 4 }}
              onClick={handleNoMatch}
            >
              Mark as No Match
            </button>
          </div>
        )}

        {!confirmedMatch && !linkedRiff && pendingMatches.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--tx-faint)' }}>
            {footage.matchStatus === 'no_match' ? 'Marked as no match.' :
             !tx ? 'Transcribe this clip to find script matches automatically.' :
             tx.status === 'complete' ? 'No matches found.' : 'Matches will appear after transcription completes.'}
          </div>
        )}

        {/* Script search — always available to editors in every match state
            (pending, confirmed, Riff, none). Pick a script to preview it against
            the transcript, then Assign to force-match this clip to it. */}
        {canEdit && (
          <div style={{ padding: '10px 12px', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8, marginTop: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--tx-muted)', fontWeight: 600 }}>Script search</div>
            {/* clientId=null → search across ALL clients' scripts. */}
            <ScriptSearch clientId={null} onPick={handleSearchPick} />
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 6 }}>
              Search every script, preview it against the transcript, then assign to force-match this clip — even if it's already matched.
            </div>
          </div>
        )}

        {!canEdit && pendingMatches.length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--tx-faint)' }}>{pendingMatches.length} match candidate{pendingMatches.length !== 1 ? 's' : ''} pending review.</div>
        )}
      </div>

      {/* Transcript text */}
      {tx?.status === 'complete' && tx.transcriptText && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--tx-muted)', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Transcript</div>
          <div style={{ fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.7, maxHeight: 160, overflowY: 'auto', background: 'var(--bg-card2)', padding: '10px 12px', borderRadius: 8 }}>
            {tx.transcriptText}
          </div>
          <div style={{ marginTop: 8 }}>
            <TranscriptDownloads
              transcriptionId={tx.id}
              transcriptText={tx.transcriptText}
              baseName={footage.fileName}
            />
          </div>
        </div>
      )}

      {/* Script preview modal — pending matches (right-side, read-only) */}
      {previewMatch && (
        <ScriptPreviewModal
          scriptId={previewMatch.scriptId}
          transcriptText={tx?.transcriptText}
          onClose={() => setPreviewMatch(null)}
        />
      )}

      {/* Script preview modal — manual search (left-side, preview-then-confirm) */}
      {searchPreview && (
        <ScriptPreviewModal
          scriptId={searchPreview.scriptId}
          transcriptText={tx?.transcriptText}
          side="left"
          onConfirm={handleManualMatchConfirm}
          confirming={confirmingSearch}
          confirmLabel="Assign this script"
          onClose={() => { if (!confirmingSearch) setSearchPreview(null); }}
        />
      )}

      {/* Delete confirmation modal */}
      {/* Archive confirm — archiving MOVES the file to cold Storage, so it says
          plainly what happens and where the file goes before doing it. */}
      {archiveConfirm && (
        <div
          onClick={() => !archiving && setArchiveConfirm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 430, background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 10, boxShadow: '0 12px 36px rgba(0,0,0,0.5)', fontFamily: 'var(--font)' }}
          >
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)' }}>Archive to Storage?</div>
              <button
                disabled={archiving}
                onClick={() => setArchiveConfirm(null)}
                style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: archiving ? 'default' : 'pointer', fontSize: 16, lineHeight: 1, padding: 4 }}
              >✕</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Move <strong style={{ color: 'var(--tx-primary)' }}>{archiveConfirm.fileName}</strong> to cold Storage.
              </p>
              <div style={{ margin: '12px 0 0', padding: '10px 12px', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--tx-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Destination</div>
                <div style={{ fontSize: 12, color: 'var(--tx-secondary)', marginTop: 5 }}>
                  {archiveConfirm.clientName} · <strong style={{ color: 'var(--tx-primary)' }}>{archiveConfirm.folderName}</strong>
                </div>
              </div>
              <ul style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.65, margin: '12px 0 0', paddingLeft: 18 }}>
                <li>The file moves to cold Storage (Glacier) and <strong>stops playing here</strong>.</li>
                <li>The clip, its transcript and its match history are kept.</li>
                <li>It can be restored from Storage at any time using Promote.</li>
              </ul>
              {/* Archiving breaks any live public link. Warn with the count
                  before the Owner confirms, not after a viewer complains. */}
              {archiveConfirm.activeShareLinks > 0 && (
                <div style={{ margin: '12px 0 0', padding: '10px 12px', background: 'rgba(212,85,85,0.10)', border: '1px solid rgba(212,85,85,0.30)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', fontWeight: 600 }}>
                    ⚠ {archiveConfirm.activeShareLinks} active share link{archiveConfirm.activeShareLinks === 1 ? '' : 's'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx-secondary)', marginTop: 4, lineHeight: 1.55 }}>
                    Anyone holding {archiveConfirm.activeShareLinks === 1 ? 'that link' : 'those links'} will see
                    "this clip has been archived" instead of the clip. The link{archiveConfirm.activeShareLinks === 1 ? '' : 's'} will
                    work again if you restore the clip from Storage.
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" disabled={archiving} onClick={() => setArchiveConfirm(null)}>Cancel</button>
              <button
                disabled={archiving}
                onClick={handleArchive}
                style={{ background: 'rgba(139,109,255,0.15)', color: 'var(--violet)', border: '1px solid rgba(139,109,255,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: archiving ? 'default' : 'pointer', fontFamily: 'var(--font)' }}
              >
                {archiving ? 'Archiving…' : 'Archive to Storage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div
          onClick={() => !deleting && setDeleteConfirm(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 400, background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 10, boxShadow: '0 12px 36px rgba(0,0,0,0.5)', fontFamily: 'var(--font)' }}
          >
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)' }}>Delete Footage?</div>
              <button
                disabled={deleting}
                onClick={() => setDeleteConfirm(false)}
                style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: deleting ? 'default' : 'pointer', fontSize: 16, lineHeight: 1, padding: 4 }}
              >✕</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Permanently delete <strong style={{ color: 'var(--tx-primary)' }}>{footage.fileName}</strong>?
              </p>
              <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                The file will be removed from S3 and the match history will be discarded. This cannot be undone.
              </p>
            </div>
            <div style={{ padding: '12px 20px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" disabled={deleting} onClick={() => setDeleteConfirm(false)}>Cancel</button>
              <button
                disabled={deleting}
                onClick={handleDelete}
                style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: deleting ? 'default' : 'pointer', fontFamily: 'var(--font)' }}
              >
                {deleting ? 'Deleting…' : 'Delete Footage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RawFootage() {
  const { user } = useAuth();
  const [footage,        setFootage]        = useState([]);
  const [total,          setTotal]          = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [clients,        setClients]        = useState([]);
  const [members,        setMembers]        = useState([]);
  const [clientFilter,   setClientFilter]   = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [uploaderFilter, setUploaderFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed the open clip from the URL so a refresh stays on that footage detail.
  const [selectedId,     setSelectedId]     = useState(() => searchParams.get('footage') || null);
  const [showUpload,     setShowUpload]      = useState(false);
  const [sortCol,        setSortCol]        = useState('Date');
  const [sortDir,        setSortDir]        = useState('desc');
  const [transcribingIds, setTranscribingIds] = useState(() => new Set());

  // Mirror the open clip into the URL (?footage=<id>) so a refresh restores the
  // footage detail instead of bouncing back to the list. selectedId is seeded
  // from the URL above, so this stays in sync as the panel opens/closes.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (selectedId) p.set('footage', selectedId); else p.delete('footage');
      return p;
    }, { replace: true });
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isExternal = user?.isExternal;
  const canUpload  = user?.roles?.some(r =>
    ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'].includes(r)
  ) || isExternal;
  // Camera-drive ingest (Context.HeliconflowCameraIngest.md): the upload roles minus external users — the API enforces the same set.
  const canIngest = !isExternal && user?.roles?.some(r =>
    ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'].includes(r)
  );

  // `silent` skips the full-screen loading state: background polls must not
  // blank the list to "Loading…" and snap it back every few seconds. Only the
  // initial load and explicit filter changes show the placeholder.
  // Bulk delete (owner order 2026-09-02). Raw Footage delete is PERMANENT —
  // it removes the cloud file and its thumbnail, no recycle bin — so the
  // warning popup says exactly that. Per-row permission (Admin, own uploader,
  // or editor cross-delete) is enforced server-side; refusals are skipped and
  // reported, never forced.
  const [selectedClips,  setSelectedClips]  = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  useEffect(() => {
    setSelectedClips(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(footage.map(c => c.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [footage]);
  function toggleClipSelect(id) {
    setSelectedClips(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function handleBulkDelete() {
    const ids = [...selectedClips];
    if (!ids.length || bulkDeleting) return;
    setBulkDeleting(true);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        await api.delete(`/api/footage/${id}`);
        deleted++;
      } catch (err) {
        const c = footage.find(x => x.id === id);
        skipped.push(`${c?.fileName || id} — ${err.response?.data?.error || 'failed'}`);
      }
    }
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setSelectedClips(new Set());
    fetchFootage();
    if (skipped.length) {
      alert(`${deleted} of ${ids.length} deleted.\n\nSkipped ${skipped.length}:\n${skipped.slice(0, 10).join('\n')}${skipped.length > 10 ? `\n…and ${skipped.length - 10} more` : ''}`);
    }
  }

  const fetchFootage = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (clientFilter)   params.set('client_id',   clientFilter);
      // "archived" is not a match_status — it maps to view=.
      if (statusFilter === 'archived') params.set('view', 'archived');
      else if (statusFilter)           params.set('match_status', statusFilter);
      if (uploaderFilter) params.set('uploaded_by',  uploaderFilter);
      // DEF-113 (Faces UI F-4): the API caps a page at 100 and the page never paged — "All" showed only the 100 newest clips, so
      // older Matched items were simply absent while the header counted them. Walk every page (newest first) until the total is in.
      const all = []; let total = 0, offset = 0;
      for (let page = 0; page < 50; page++) {
        params.set('offset', String(offset));
        const r = await api.get(`/api/footage?${params}`);
        const rows = r.data.footage || []; total = r.data.total || 0;
        all.push(...rows); offset += rows.length;
        if (rows.length < 100 || all.length >= total) break;
      }
      setFootage(all);
      setTotal(total);
    } catch { /* non-fatal */ }
    finally { if (!silent) setLoading(false); }
  }, [clientFilter, statusFilter, uploaderFilter]);

  useEffect(() => { fetchFootage(); }, [fetchFootage]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // Poll the list while any row is in pending/processing so the column updates without manual refresh.
  const anyTranscribing = useMemo(
    () => footage.some(c => c.transcriptionStatus === 'pending' || c.transcriptionStatus === 'processing'),
    [footage]
  );
  useEffect(() => {
    if (!anyTranscribing) return;
    const id = setInterval(() => { fetchFootage({ silent: true }); }, 8000);
    return () => clearInterval(id);
  }, [anyTranscribing, fetchFootage]);

  async function handleTranscribeRow(clipId, e) {
    e.stopPropagation();
    if (transcribingIds.has(clipId)) return;
    setTranscribingIds(prev => { const next = new Set(prev); next.add(clipId); return next; });
    // Optimistic: flip this row to 'pending' so the cell reflects intent instantly.
    setFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: 'pending' } : c));
    try {
      await api.post('/api/transcriptions', { source_type: 'raw_footage', source_id: clipId });
    } catch (err) {
      alert(err.response?.data?.error || 'Transcription failed to start.');
      setFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: c.transcriptionStatus === 'pending' ? null : c.transcriptionStatus } : c));
    } finally {
      setTranscribingIds(prev => { const next = new Set(prev); next.delete(clipId); return next; });
    }
  }

  // Match-status sort order: most-actionable first.
  const MATCH_STATUS_RANK = { pending_review: 0, unmatched: 1, matched: 2, no_match: 3 };
  // Transcribed sort order: "No" first (actionable), then in-progress, then failed, then complete.
  const TRANSCRIBED_RANK = { failed: 2, pending: 1, processing: 1, complete: 3 };

  const sortedFootage = useMemo(() => {
    if (!sortCol) return footage;
    const dir = sortDir === 'asc' ? 1 : -1;
    const key = (c) => {
      switch (sortCol) {
        case 'File':         return (c.fileName || '').toLowerCase();
        case 'Project':       return (c.clientName || '').toLowerCase();
        case 'Transcribed':  return TRANSCRIBED_RANK[c.transcriptionStatus] ?? 0;
        case 'Match Status': return MATCH_STATUS_RANK[c.matchStatus] ?? 99;
        case 'Uploader':     return (c.uploaderName || '').toLowerCase();
        case 'Date':         return c.createdAt || '';
        default:             return '';
      }
    };
    return [...footage].sort((a, b) => {
      const av = key(a), bv = key(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [footage, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'Date' ? 'desc' : 'asc'); }
  }

  const STATUS_PILLS = [
    { value: '', label: 'All' },
    { value: 'unmatched',      label: 'Unmatched' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'matched',        label: 'Matched' },
    { value: 'no_match',       label: 'No Match' },
    // Faces UI F-5 (D-154): the "✂ Clips" pill (view=clips — auto-extracted child clips only) is gone: every child clip already
    // sits in the list under its "All" / match-status pill with the ↳ ✂ marker, and each parent row carries its "✂ N" badge.
    { value: 'archived',       label: '📁 Archived' },
  ];

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', fontFamily: 'var(--font)', color: 'var(--tx-primary)' }}>

      {/* Page header */}
      <div style={{ flexShrink: 0, padding: '20px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-primary)', margin: 0 }}>Raw Footage</h1>
          <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 2 }}>{total} clip{total !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {canIngest && (
            <Link to="/footage/ingest" className="btn-ghost" style={{ fontSize: 13, textDecoration: 'none' }} data-testid="open-camera-ingest">⇪ Ingest from camera drive</Link>
          )}
          {canUpload && (
            <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setShowUpload(v => !v)}>
              {showUpload ? '✕ Close Upload' : '+ Upload Footage'}
            </button>
          )}
        </div>
      </div>

      {/* Upload panel */}
      {showUpload && (
        <div style={{ flexShrink: 0, padding: '16px 28px 0' }}>
          <UploadZone clients={clients} onUploaded={() => { fetchFootage(); }} />
        </div>
      )}

      {/* Filters */}
      <div style={{ flexShrink: 0, padding: '16px 28px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {/* Status pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_PILLS.map(p => (
            <button
              key={p.value}
              data-testid="footage-status-pill" data-value={p.value || 'all'} aria-pressed={statusFilter === p.value}
              onClick={() => setStatusFilter(p.value)}
              style={{
                padding: '5px 12px', borderRadius: 100, fontSize: 12, fontFamily: 'var(--font)',
                cursor: 'pointer', border: '1px solid',
                background: statusFilter === p.value ? 'var(--violet)' : 'transparent',
                color:      statusFilter === p.value ? '#fff' : 'var(--tx-secondary)',
                borderColor: statusFilter === p.value ? 'var(--violet)' : 'var(--border-mid)',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Client filter */}
        {!isExternal && (
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '6px 10px', color: 'var(--tx-secondary)', fontSize: 12, fontFamily: 'var(--font)' }}
          >
            <option value="">All projects</option>
            <option value="unassigned">Unassigned</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        {/* Uploader filter */}
        <select
          value={uploaderFilter}
          onChange={e => setUploaderFilter(e.target.value)}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '6px 10px', color: 'var(--tx-secondary)', fontSize: 12, fontFamily: 'var(--font)' }}
        >
          <option value="">All uploaders</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {/* Content area: list + detail */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* Footage list */}
        <div style={{ flex: selectedId ? '0 0 60%' : '1', display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto', borderRight: selectedId ? '1px solid var(--border)' : 'none' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>Loading…</div>
          ) : footage.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>
              No footage found.{canUpload ? ' Upload your first clip above.' : ''}
            </div>
          ) : (
            <>
            {selectedClips.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--border)', background: 'rgba(212,85,85,0.05)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)' }}>
                  {selectedClips.size} clip{selectedClips.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={bulkDeleting}
                  style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
                >
                  Delete {selectedClips.size} clip{selectedClips.size !== 1 ? 's' : ''}…
                </button>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelectedClips(new Set())} disabled={bulkDeleting}>
                  Clear
                </button>
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-app)', zIndex: 1 }}>
                  <th style={{ padding: '10px 0 10px 14px', width: 26 }}>
                    <input
                      type="checkbox"
                      title="Select all visible clips"
                      checked={sortedFootage.length > 0 && sortedFootage.every(c => selectedClips.has(c.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedClips(new Set(sortedFootage.map(c => c.id)));
                        else setSelectedClips(new Set());
                      }}
                      style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                    />
                  </th>
                  {['', 'File', 'Project', 'Transcribed', 'Match Status', 'Uploader', 'Date'].map(h => {
                    const sortable = h !== '';
                    const active = sortCol === h;
                    return (
                      <th
                        key={h}
                        onClick={sortable ? () => toggleSort(h) : undefined}
                        style={{
                          padding: '10px 14px', fontSize: 11, fontWeight: 600,
                          color: active ? 'var(--tx-secondary)' : 'var(--tx-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          textAlign: 'left', whiteSpace: 'nowrap',
                          cursor: sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                      >
                        {h}
                        {active && <span style={{ marginLeft: 6, color: 'var(--violet)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedFootage.map(clip => (
                  <tr
                    key={clip.id}
                    data-testid="footage-row" data-match-status={clip.matchStatus || ''}
                    onClick={() => setSelectedId(prev => prev === clip.id ? null : clip.id)}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: selectedId === clip.id ? 'rgba(139,109,255,0.08)' : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (selectedId !== clip.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (selectedId !== clip.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '8px 0 8px 14px', width: 26 }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedClips.has(clip.id)}
                        onChange={() => toggleClipSelect(clip.id)}
                        style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '8px 14px', width: 60 }}>
                      {clip.thumbnailUrl
                        ? <img src={clip.thumbnailUrl} alt="" style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 5, display: 'block' }} />
                        : mediaKindOf(clip) === 'audio'
                          ? <AudioTile fileName={clip.fileName} width={56} height={32} />
                          : <div style={{ width: 56, height: 32, background: 'var(--bg-card)', borderRadius: 5 }} />
                      }
                    </td>
                    <td style={{ padding: '8px 14px', maxWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: clip.parentFootageId ? 14 : 0 }}>
                        {clip.parentFootageId && (
                          <span title="Auto-extracted scene clip" style={{ fontSize: 11, color: 'var(--tx-faint)', flexShrink: 0 }}>↳ ✂</span>
                        )}
                        <div style={{ fontSize: 13, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clip.fileName}</div>
                        {clip.clipCount > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 100, background: 'rgba(139,109,255,0.12)', color: 'var(--violet)', border: '1px solid rgba(139,109,255,0.25)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            ✂ {clip.clipCount}
                          </span>
                        )}
                        {clip.isPossibleDuplicate && (
                          <span
                            title={clip.duplicateOfName ? `Possible duplicate of ${clip.duplicateOfName}` : 'Possible duplicate upload'}
                            style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 100, background: 'rgba(139,109,255,0.12)', color: 'var(--violet)', border: '1px solid rgba(139,109,255,0.25)', whiteSpace: 'nowrap', flexShrink: 0 }}
                          >⧉ Duplicate?</span>
                        )}
                        {(clip.uploadStatus === 'failed' || clip.verificationStatus === 'mismatch' || clip.verificationStatus === 'missing') && (
                          <span
                            title={clip.verificationError || 'Upload failed verification'}
                            style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 100, background: 'rgba(255,90,90,0.12)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(255,90,90,0.25)', whiteSpace: 'nowrap', flexShrink: 0 }}
                          >⚠ Unverified</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--tx-muted)', paddingLeft: clip.parentFootageId ? 14 : 0 }}>
                        {clip.sceneLabel ? `${clip.sceneLabel} · ` : ''}{formatBytes(clip.fileSizeBytes)}
                      </div>
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: 12, color: 'var(--tx-secondary)', whiteSpace: 'nowrap' }}>
                      {clip.clientName || '—'}
                    </td>
                    <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const tx = clip.transcriptionStatus;
                        const busy = transcribingIds.has(clip.id);
                        if (tx === 'complete') {
                          return (
                            <span style={{
                              fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 100,
                              background: 'rgba(72,190,110,0.12)', color: 'var(--green)',
                              border: '1px solid rgba(72,190,110,0.25)',
                            }}>Yes</span>
                          );
                        }
                        if (tx === 'pending' || tx === 'processing') {
                          return (
                            <span style={{ fontSize: 11, color: 'var(--tx-muted)', fontStyle: 'italic' }}>
                              Transcribing…
                            </span>
                          );
                        }
                        // null/undefined → "No" (clickable), 'failed' → "Retry" (clickable)
                        const isRetry = tx === 'failed';
                        return (
                          <button
                            onClick={(e) => handleTranscribeRow(clip.id, e)}
                            disabled={busy}
                            title={isRetry ? 'Retry transcription' : 'Click to transcribe'}
                            style={{
                              fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 100,
                              background: isRetry ? 'rgba(255,140,90,0.10)' : 'rgba(255,255,255,0.04)',
                              color: isRetry ? 'var(--orange, #ff8c5a)' : 'var(--tx-secondary)',
                              border: '1px solid',
                              borderColor: isRetry ? 'rgba(255,140,90,0.30)' : 'var(--border)',
                              cursor: busy ? 'wait' : 'pointer',
                              opacity: busy ? 0.6 : 1,
                              fontFamily: 'inherit',
                            }}
                          >
                            {isRetry ? 'Retry' : 'No'}
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 100,
                        background: clip.matchStatus === 'matched' ? 'rgba(72,190,110,0.12)' :
                                    clip.matchStatus === 'pending_review' ? 'rgba(139,109,255,0.12)' :
                                    clip.matchStatus === 'no_match' ? 'rgba(255,255,255,0.05)' : 'transparent',
                        color: MATCH_STATUS_COLORS[clip.matchStatus],
                        border: '1px solid',
                        borderColor: clip.matchStatus === 'matched' ? 'rgba(72,190,110,0.25)' :
                                     clip.matchStatus === 'pending_review' ? 'rgba(139,109,255,0.25)' : 'var(--border)',
                      }}>
                        {MATCH_STATUS_LABELS[clip.matchStatus] || clip.matchStatus}
                        {clip.pendingMatchCount > 0 && ` (${clip.pendingMatchCount})`}
                      </span>
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: 12, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
                      {clip.uploaderName || '—'}
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: 11, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
                      {fmtDate(clip.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>

        {/* Bulk-delete warning popup — Raw Footage delete is PERMANENT: the
            cloud file and its thumbnail are removed, there is no recycle bin. */}
        {bulkDeleteOpen && (
          <div className="modal-overlay" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
              <div className="modal-header">
                <div className="modal-title" style={{ color: 'var(--s-incomplete-t)' }}>
                  ⚠ Permanently delete {selectedClips.size} clip{selectedClips.size !== 1 ? 's' : ''}?
                </div>
                <button className="vc-close" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: 13, color: 'var(--tx-primary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {`This deletes the video files and their thumbnails from cloud storage and removes them from Raw Footage.\n\nUnlike Storage, there is NO recycle bin here — this cannot be undone.\n\nClips you don't have permission to delete will be skipped and listed.`}
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
                >
                  {bulkDeleting ? 'Deleting…' : `Permanently delete ${selectedClips.size}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail panel */}
        {selectedId && (
          <div style={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: 'var(--bg-base)' }}>
            <DetailPanel
              key={selectedId}
              footageId={selectedId}
              clients={clients}
              onClose={() => setSelectedId(null)}
              onUpdated={() => { fetchFootage(); }}
              onOpenFootage={(id) => setSelectedId(id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
